<template>
  <div>
    <q-field v-if="error" :error="error" :error-label="errorLabel">
      <q-search
        type="text"
        v-model="inputField"
        :placeholder="searchString"
        :disable="disable"
        :float-label="name"
      >
        <q-autocomplete
          :max-results="25"
          :min-characters="2"
          @search="search"
          @selected="selected"
        />
      </q-search>
    </q-field>

    <q-field v-else :helper="helper">
      <q-search
        type="text"
        v-model="inputField"
        :placeholder="searchString"
        :disable="disable"
        :float-label="name"
      >
        <q-autocomplete
          :max-results="25"
          :min-characters="2"
          @search="search"
          @selected="selected"
        />
      </q-search>
    </q-field>
    <div class="row q-mt-sm">
      <div
        class="q-mr-sm q-pb-sm"
        v-for="(item, i) in selectedItems"
        :key="`${i}-${item.label}`"
        :draggable="draggable"
        @dragstart="dragstart(item, $event)"
      >
        <q-chip
          :readonly="disable"
          small
          :closable="!disable"
          color="primary"
          @click="emitClick(item)"
          @hide="removeItem(i)"
        >
          {{ item.label }}
        </q-chip>
      </div>
    </div>
  </div>
</template>

<script>
import _get from 'lodash.get';
import EventBus from '@/event-bus';

export default {
  props: {
    disable: {
      type: Boolean,
      default: false,
    },

    helper: {
      type: String,
      required: false,
    },

    draggable: {
      type: Boolean,
      default: false,
    },

    error: {
      type: Boolean,
      required: false,
    },

    allowAdd: {
      type: Boolean,
      default: true,
    },

    // Emit an event rather than posting a new item when selecting a custom element
    customPost: {
      type: Boolean,
      default: false,
    },

    errorLabel: {
      type: String,
      required: false,
    },
    // Given selection by saved content.
    selection: {
      type: Array,
      default: () => [],
    },
    // Some items use the term 'label' and others use 'name' for adding new items.
    labelTerm: {
      type: String,
      required: true,
    },

    // Name describes the item being added. eg. tag, category...
    name: {
      type: String,
      required: true,
    },

    // Where the request is made to.
    endpoint: {
      type: String,
      required: true,
    },

    promptNewMessage: {
      type: String,
      default: '',
    },

    // JSON key of the array result. eg. categories
    resultLabel: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      foundItems: [],
      selectedItems: [],
      inputField: '',
    };
  },

  computed: {
    searchString() {
      return this.$store.state.stringsStore.language === 'nl' ? 'Zoek' : 'Search';
    },
  },

  methods: {
    emitClick(item) {
      const allItems = [...this.foundItems, this.selection];
      const found = allItems.find(el => el.id === item.id);
      this.$emit('chipClicked', found);
    },

    async search(terms, done) {
      try {
        // Request items based on terms given
        const results = await this.axios.get(
          `${this.endpoint}?autocomplete=${terms.toLowerCase()}`,
        );

        this.foundItems = results.data[this.resultLabel];

        // Map only the item names/labels... to the suggested array
        const suggested = results.data[this.resultLabel].map(item => ({
          label: item[this.labelTerm],
          id: item.id,
        }));

        // Suggested items found, return suggestions.
        if (suggested.length) {
          // filter out previously selected items
          const filtered = suggested.filter(val => {
            const labels = this.selectedItems.map(item => item.label);
            return !labels.includes(val.label);
          });

          // Callback comparator for suggestions to entered term
          const isNotEqual = a => a.label.toLowerCase() !== terms.toLowerCase();
          // If the entered term is not the same as any suggested, allow user to add the term
          if (this.allowAdd) {
            if (suggested.every(isNotEqual) && this.promptNewMessage !== '') {
              filtered.push({
                label: `${this.promptNewMessage} - "${terms}"`,
                value: terms,
                new: true,
              });
            }
          }

          done(filtered);
        }

        // Since an error is thrown when no items are found in autocomplete -> promt new suggestion here.
      } catch (error) {
        if (this.promptNewMessage !== '' && this.allowAdd) {
          done([{ label: `${this.promptNewMessage} - "${terms}"`, value: terms, new: true }]);
        } else {
          done([]);
        }
      }
    },

    selected(item, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;

      if (item.new) {
        if (this.customPost) {
          const add = { value: item.value, label: item.value };
          this.$emit('add', add);
          return;
        }
        this.axios
          .post(this.endpoint, `${this.labelTerm}=${item.value}`)
          .then(res => {
            this.$q.notify({
              message: `*Added a new ${this.name} - "${item.value}"`,
              timeout: 2000,
              type: 'positive',
              position: 'top-right',
            });

            this.selectedItems.push({
              label: item.value,
              id: res.headers.location.split('/').pop(),
            });
          })
          .catch(error => {
            if (_get(error, 'response.data.fields.label.code') === 'DUPLICATE') {
              this.$q.notify({
                message: `*The ${this.name} already exists`,
                timeout: 2000,
                type: 'warning',
                position: 'top-right',
              });
            }
          });
      } else {
        this.selectedItems.push(item);
      }

      // Broadcast selection to parent component
      EventBus.$emit(`chips-autocomplete:${this.resultLabel}`, this.selectedItems);

      this.$emit('change', this.selectedItems);
      this.$emit('updateAutocomplete', this.selectedItems);

      this.inputField = '';
    },

    removeItem(index) {
      if (this.disable) {
        return;
      }
      this.$emit('removed', this.selectedItems[index]);

      this.selectedItems.splice(index, 1);

      // Broadcast selection to parent component
      EventBus.$emit(`chips-autocomplete:${this.resultLabel}`, this.selectedItems);

      this.$emit('updateAutocomplete', this.selectedItems);
    },
  },

  created() {
    // Map selection from saved content.
    this.selectedItems = this.selection.map(item => ({
      label: item[this.labelTerm],
      id: item.id,
      value: item.value,
    }));
  },
};
</script>
